import { useState, useMemo } from 'react';
import { OvColumn } from '../rebalancePage/sharedTypes';

/**
 * Represents the pinned columns configuration.
 */
interface PinnedColumns {
  /** Array of accessor keys for columns pinned to the left. */
  left: (keyof any)[];
  /** Array of accessor keys for columns pinned to the right. */
  right: (keyof any)[];
}

/**
 * Represents the partitioned columns based on pinning.
 * @template T The type of the data in the table rows.
 */
interface PartitionedColumns<T> {
  /** Array of columns pinned to the left. */
  leftColumns: OvColumn<T>[];
  /** Array of columns that are not pinned. */
  normalColumns: OvColumn<T>[];
  /** Array of columns pinned to the right. */
  rightColumns: OvColumn<T>[];
}

/**
 * Represents the result of the `usePinnedColumns` hook.
 * @template T The type of the data in the table rows.
 */
interface UsePinnedColumnsResult<T> {
  /** The current pinned columns configuration. */
  pinnedColumns: PinnedColumns;
  /** Function to toggle the pinning of a column. */
  togglePinColumn: (accessor: keyof T, position?: 'left' | 'right') => void;
  /** Function to get the pinning position of a column. */
  getColumnPosition: (accessor: keyof T) => 'left' | 'right' | 'normal';
  /** The columns partitioned into left, normal, and right arrays. */
  partitionedColumns: PartitionedColumns<T>;
}

/**
 * A React hook for managing pinned columns in a table.
 * @template T The type of the data in the table rows.
 * @param {Column<T>[]} initialColumns The initial array of column definitions.
 * @returns {UsePinnedColumnsResult<T>} An object containing pinned column state and utility functions.
 */
const usePinnedColumns = <T extends { [key: string]: any }>(initialColumns: OvColumn<T>[]): UsePinnedColumnsResult<T> => {
  const [pinnedColumns, setPinnedColumns] = useState<PinnedColumns>({
    left: [],
    right: [],
  });

  /**
   * Toggles the pinning of a column.
   * @param {keyof T} accessor The accessor key of the column to toggle.
   * @param {'left' | 'right'} [position='left'] The position to pin the column to.
   */
  const togglePinColumn = (accessor: keyof T, position: 'left' | 'right' = 'left') => {
    setPinnedColumns((prev) => {
      const newPinned: PinnedColumns = {
        left: [...prev.left],
        right: [...prev.right],
      };
      const isPinnedLeft = prev.left.includes(accessor);
      const isPinnedRight = prev.right.includes(accessor);

      newPinned.left = newPinned.left.filter((col) => col !== accessor);
      newPinned.right = newPinned.right.filter((col) => col !== accessor);

      if (!(isPinnedLeft && position === 'left') && !(isPinnedRight && position === 'right')) {
        if (position === 'left') {
          newPinned.left.push(accessor);
        } else if (position === 'right') {
          newPinned.right.push(accessor);
        }
      }

      return newPinned;
    });
  };

  /**
   * Gets the pinning position of a column.
   * @param {keyof T} accessor The accessor key of the column.
   * @returns {'left' | 'right' | 'normal'} The pinning position of the column.
   */
  const getColumnPosition = (accessor: keyof T): 'left' | 'right' | 'normal' => {
    if (pinnedColumns.left.includes(accessor)) return 'left';
    if (pinnedColumns.right.includes(accessor)) return 'right';
    return 'normal';
  };

  /**
   * Partitions the columns into left, normal, and right arrays based on pinning.
   */
  const partitionedColumns = useMemo<PartitionedColumns<T>>(() => {
    const leftColumns = initialColumns.filter((col) => pinnedColumns.left.includes(col.accessor));
    const rightColumns = initialColumns.filter((col) => pinnedColumns.right.includes(col.accessor));
    const normalColumns = initialColumns.filter((col) => !pinnedColumns.left.includes(col.accessor) && !pinnedColumns.right.includes(col.accessor));

    return { leftColumns, normalColumns, rightColumns };
  }, [initialColumns, pinnedColumns]);

  return {
    pinnedColumns,
    togglePinColumn,
    getColumnPosition,
    partitionedColumns,
  };
};

export default usePinnedColumns;
